import React, { useRef, useState, useEffect, useMemo, useImperativeHandle } from "react";
import { DatePicker, Schema, SelectPicker, Tooltip } from "rsuite";
import styled from "styled-components";

import { SP_KEY__INCORPORATED, SP_KEY__GLOBAL_ACCESS } from "const";
import { toStartDayUTCTime } from "utils";
import { useWindowWidth } from "hooks";
import { api } from "api/loginRoutes";
import Checkbox from "hoc/Checkbox";
import ServicePlans from "components/Filters/ServicePlans";
import CustomField from "components/Form/CustomField";
import RateField from "components/Form/RateField";
import Form from "components/Form/CompatForm";
import Whisper from "components/Whisper";
import Modal from "components/Modal";


const { NumberType, StringType } = Schema.Types;

// TODO: Rewrite this component to use pickSupplierPrice and pickedSupplierPrice props
const AddNewRate = ({
    show,
    setShow,
    subdestination_list,
    accountId,
    e212List,
    updateItems,
    isSupplier,
    pickPrice,
    pickedPrice,
    supplierPriceList
}) => {

    const formRef = useRef(null);
    const [disabled, setDisabled] = useState(false);
    const [extraDisabled, setExtraDisabled] = useState(false);

    const onSubmit = async () => {
        if (!formRef.current.check())
            return;

        const data = { ...formRef.current.getFormValue() };

        setDisabled(true);

        const [mcc, mnc] = data.mccMnc ? data.mccMnc.split("-") : [];

        if (data.mccMnc) {
            delete data.mccMnc;
        }

        if (data.withDate && data.start_date) {
            data.start_date = toStartDayUTCTime(new Date(data.start_date));
        } else {
            delete data.start_date;
        }

        if (data.withDate && data.end_date) {
            data.end_date = toStartDayUTCTime(new Date(new Date(data.end_date).setDate(new Date(data.end_date).getDate() + 1)));
        } else {
            delete data.end_date;
        }

        delete data.withDate;
        delete data["sms.supplier_price_id"];

        const method = isSupplier ? "sms.supplier_price_mccmnc:create" : "account_price:create";


        const params = isSupplier
            ? {
                target: {
                    "sms.supplier_price_id": pickedPrice?.id
                },
                ...data,
                mcc,
                mnc,
                rate: +data.rate
            }
            : {
                target: {
                    account_id: accountId
                },
                ...data,
                rate: +data.rate
            };

        const result = await api(method, params);

        setDisabled(false);

        if (result) {
            setShow(false);
            updateItems();
        }
    };

    return (
        <Modal
            show={show}
            title="Add new special rate"
            onClose={() => setShow(false)}
            onSuccess={onSubmit}
            successText="Create"
            extraDisabled={extraDisabled}
            width={700}
            footer
        >
            <ModalForm
                formRef={formRef}
                {...{
                    e212List,
                    subdestination_list,
                    disabled,
                    isSupplier,
                    pickedPrice,
                    supplierPriceList,

                    pickPrice,
                    setExtraDisabled
                }}
            />
        </Modal>
    );
};

export default AddNewRate;

const ModalForm = ({
    disabled = false,
    formRef,
    e212List,
    subdestination_list,
    isSupplier,
    pickedPrice,
    supplierPriceList,

    pickPrice,
    setExtraDisabled
}) => {
    const defaultFormValue = useMemo(() => {
        return isSupplier ? {
            update_transit_prefixes: false,
            "sms.supplier_price_id": pickedPrice?.id
        } : {
            update_allocated_numbers: false
        };
    }, [isSupplier, pickedPrice?.id]);

    const [withDate, setWithDate] = useState(false);
    const [formState, setFormState] = useState(defaultFormValue);
    const [selectedOperator, setSelectedOperator] = useState(null);
    const formElementRef = useRef(null);
    const windowWidth = useWindowWidth();

    useImperativeHandle(formRef, () => ({
        check: (...args) => formElementRef.current && formElementRef.current.check(...args),
        getFormValue: () => formState,
        root: formElementRef.current && formElementRef.current.root
    }), [formState]);

    useEffect(() => {
        if (!withDate) {
            setExtraDisabled(false);
        } else if (withDate && formState.start_date) {
            setExtraDisabled(false);
        } else if (withDate && !formState.start_date) {
            setExtraDisabled(true);
        }
    }, [withDate, formState.start_date, setExtraDisabled]);

    const formModel = useMemo(() => {
        return Schema.Model({
            ...(!isSupplier ? { sp_key: NumberType().isRequired("Required") } : {}),
            ...(!isSupplier ? { sde_key: NumberType().isRequired("Required") } : { mccMnc: StringType().isRequired("Required") }),
            rate: NumberType().isRequired("Required"),
        });
    }, [isSupplier]);

    const dateRanges = useMemo(() => {
        return [
            {
                label: "Today",
                value: new Date()
            },
            {
                label: "Tomorrow",
                value: new Date(new Date().setDate(new Date().getDate() + 1))
            }
        ];
    }, []);

    const handleWithDateChange = (checked) => {
        setWithDate(checked);

        setFormState(prevState => ({
            ...prevState,
            withDate: checked,
            ...(!checked ? {
                start_date: null,
                end_date: null
            } : {})
        }));

        if (!checked && formElementRef.current && formElementRef.current.cleanErrors) {
            formElementRef.current.cleanErrors();
        }
    };

    const operatorList = e212List && e212List.length ? e212List.map(value => {
        return {
            ...value,
            operatorMccMnc: `${value.mccmnc_name} (${value.mcc}${value.mnc})`,
            mccMnc: `${value.mcc}-${value.mnc}`
        };
    }) : [];

    return (
        <Form
            ref={formElementRef}
            layout="inline"
            model={formModel}
            formValue={formState}
            formDefaultValue={defaultFormValue}
            onChange={(formValue) => {
                setFormState(formValue);
            }}
        >
            {isSupplier ?
                <StyledGroupContainer>
                    <CustomField
                        accepter={SelectPicker}
                        data={supplierPriceList}
                        cleanable={false}
                        searchable={false}
                        valueKey="id"
                        labelKey="name"
                        placeholder="Select trunk type"
                        label="Trunk type"
                        name="sms.supplier_price_id"
                        onChange={(id) => {
                            const priceValue = supplierPriceList.find(price => price?.id === id);

                            pickPrice(priceValue);
                        }}
                        style={{
                            width: 230
                        }}
                        menuStyle={{ width: 320 }}
                    />
                </StyledGroupContainer> : <ServicePlans
                    excluded={[SP_KEY__INCORPORATED, SP_KEY__GLOBAL_ACCESS]}
                    disabled={disabled}
                    classPrefix="minWidth"
                />
            }

            {isSupplier ? <Whisper
                trigger="hidden"
                speaker={<Tooltip visible={!!selectedOperator}>{selectedOperator}</Tooltip>}
            >
                <StyledGroupContainer>
                    <CustomField
                        disabled={disabled}
                        accepter={SelectPicker}
                        data={operatorList}
                        onChange={(value) => {
                            setSelectedOperator(value);
                        }}
                        labelKey="operatorMccMnc"
                        valueKey="mccMnc"
                        placeholder="Operator"
                        errorPlacement="topRight"
                        name="mccMnc"
                        // classPrefix="minWidth"
                        style={{
                            width: 300
                        }}
                        menuStyle={{ width: 360 }}
                        label="Operator"
                    />
                </StyledGroupContainer>
            </Whisper> : <CustomField
                disabled={disabled}
                accepter={SelectPicker}
                data={subdestination_list}
                labelKey="name"
                valueKey="sde_key"
                placeholder="Subdestination"
                errorPlacement="topRight"
                name="sde_key"
                classPrefix="minWidth"
                menuStyle={{ width: 320 }}
            />}
            <br />
            <StyledGroupContainer>
                <RateField
                    name="rate"
                    label="Rate"
                    size="xl"
                />
            </StyledGroupContainer>
            <br />
            <Checkbox
                name="withDate"
                onChange={handleWithDateChange}
            >
                {isSupplier ? "From Date" : "For period"}
            </Checkbox>

            <CustomField
                accepter={DatePicker}
                placeholder="Enter Date"
                label="from"
                name="start_date"
                disabled={!withDate}
                ranges={dateRanges}
                // defaultValue={new Date(new Date().setHours(0, 0, 0, 0))}
                disabledDate={(date) => {
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    return date < today;
                }}

                // mobile
                oneTap={windowWidth < 480 && withDate}
                inline={windowWidth < 480 && withDate}
            />

            {!isSupplier ? <HintTextContainer>
                {!formState.end_date ? <Whisper
                    speaker={<Tooltip>Can be empty</Tooltip>}
                    trigger="hover"
                    placement="bottomStart"
                >
                    <div className="hint">*</div>
                </Whisper> : <></>}
                <CustomField
                    accepter={DatePicker}
                    placeholder="Enter Date"
                    label="to"
                    name="end_date"
                    disabled={!withDate}
                    ranges={dateRanges}
                    // defaultValue={new Date(new Date().setHours(23, 59, 59, 59))}
                    disabledDate={(date) => {
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        return date < today;
                    }}

                    // mobile
                    oneTap={windowWidth < 480 && withDate}
                    inline={windowWidth < 480 && withDate}
                />
            </HintTextContainer> : <></>}

            {!isSupplier
                ? <Checkbox name="update_allocated_numbers">
                    Update for all allocated numbers
                </Checkbox>
                : <Checkbox name="update_transit_prefixes">
                    Update for all allocated prefixes
                </Checkbox>}
        </Form>
    );
};

const StyledGroupContainer = styled.div`
    && {
        display: inline;

        .rs-form-group {
            margin-left: 15px;
            margin-bottom: 20px;
        }
    }
`;


const HintTextContainer = styled.div`
    && {
        display: inline-block;

        .hint {
            display: block;
            position: absolute;
            color: var(--color-green);
            font-size: 11px;
            margin-top: 8px;
            margin-left: -5px;
            width: 24px;
            height: 24px;
            text-align: end;
        }
    }
`;
